/* Sample Selection only. Checkout eligibility must also be enforced by a Shopify validation function. */
(() => {
  'use strict';
  if (window.elCartSamplesLoaded) return;
  window.elCartSamplesLoaded = true;
  const roots = () => [...document.querySelectorAll('[data-el-samples]')];
  const initial = document.querySelector('[data-el-sample-config]') || roots()[0];
  if (!initial) return;
  const ids = new Set(initial.dataset.sampleIds.split(',').filter(Boolean).map(Number));
  const thresholds = { [initial.dataset.currency]: Number(initial.dataset.threshold) };
  const route = window.Shopify?.routes?.root || '/';
  let busy = false, pending = false, timer, currentCart, message = '';
  const helper = () => window.Alpine?.store('xCartHelper');
  const samples = cart => cart.items.filter(item => ids.has(Number(item.variant_id)));
  const eligible = cart => {
    const threshold = thresholds[cart.currency] || 0;
    const paidTotal = cart.total_price - samples(cart).reduce((sum, item) => sum + item.final_line_price, 0);
    return threshold > 0 && paidTotal >= threshold;
  };
  // Use line keys, including duplicate variants with different line properties.
  function corrections(cart, keepId) {
    const updates = {};
    let kept = false;
    for (const item of samples(cart)) {
      const keep = eligible(cart) && !kept && (keepId === undefined || Number(item.variant_id) === keepId);
      const quantity = keep ? 1 : 0;
      if (keep) kept = true;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    }
    return updates;
  }
  function paint(cart = currentCart) {
    const chosen = cart ? samples(cart)[0]?.variant_id : undefined;
    for (const root of roots()) {
      if (cart) root.hidden = !eligible(cart);
      root.setAttribute('aria-busy', String(busy));
      for (const button of root.querySelectorAll('[data-el-sample]')) {
        const selected = cart ? Number(button.dataset.elSample) === Number(chosen) : button.getAttribute('aria-pressed') === 'true';
        button.disabled = busy;
        button.setAttribute('aria-pressed', String(selected));
        button.querySelector('[data-el-sample-label]').textContent = selected ? 'Selected · Remove' : 'Select';
      }
      root.querySelector('[data-el-sample-status]').textContent = message;
    }
  }
  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(route + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        signal: controller.signal, ...(body ? { body: JSON.stringify(body) } : {})
      });
      const data = await response.json();
      if (!response.ok) throw new Error('Cart request failed');
      return data;
    } finally { clearTimeout(timeout); }
  }
  async function waitForNativeCart() {
    const start = Date.now();
    while (window.updatingCart || window.updatingEstimate || window.Alpine?.store('xMiniCart')?.loading) {
      if (Date.now() - start > 12000) throw new Error('Cart is still updating');
      await new Promise(resolve => setTimeout(resolve, 60));
    }
  }
  function sectionIds() {
    // Shopify bundled rendering accepts at most five sections.
    return helper()?.getSectionsToRender().map(section => section.id).filter(id =>
      !id.endsWith('cart-upsell') && id !== 'cart-icon-bubble-mobile-dock'
    ).slice(0, 5) || ['cart-drawer', 'cart-icon-bubble'];
  }
  async function update(updates, renderSections = true) {
    return request('cart/update.js', { updates, ...(renderSections ? { sections: sectionIds(), sections_url: location.pathname } : {}) });
  }
  async function render(cart) {
    if (helper()) helper().currentItemCount = cart.item_count;
    if (cart.sections && helper()) await helper().reRenderSections(cart.sections);
    currentCart = cart;
    paint(cart);
    document.dispatchEvent(new CustomEvent('eurus:cart:items-changed', { detail: { elSamples: true } }));
  }
  async function reconcile(chooseId) {
    await waitForNativeCart();
    window.updatingCart = true;
    try {
      let cart = await request('cart.js');
      let desired = chooseId;
      if (desired !== undefined && !eligible(cart)) desired = 0;
      if (desired && samples(cart).some(item => Number(item.variant_id) === desired)) desired = 0;
      let changes = corrections(cart, desired);
      const changed = Object.keys(changes).length > 0;
      if (changed) cart = await update(changes, !desired);
      if (desired && !samples(cart).some(item => Number(item.variant_id) === desired)) {
        const added = await request('cart/add.js', { items: [{ id: desired, quantity: 1 }], sections: sectionIds(), sections_url: location.pathname });
        // Recheck after add; another tab or an app may have changed the basket.
        cart = await request('cart.js');
        changes = corrections(cart, desired);
        if (Object.keys(changes).length) cart = await update(changes);
        else cart.sections = added.sections;
      }
      if (changed || desired) await render(cart);
      else { currentCart = cart; paint(cart); }
      return cart;
    } finally { window.updatingCart = false; }
  }
  async function run(chooseId) {
    if (busy) { if (chooseId === undefined) pending = true; return; }
    busy = true; message = ''; paint();
    try { await reconcile(chooseId); }
    catch (_) { message = 'Unable to update your sample. Please refresh your cart and try again.'; }
    finally {
      busy = false; paint();
      if (pending) { pending = false; schedule(); }
    }
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(() => run(), 180); }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-el-sample]');
    if (!button) return;
    event.preventDefault();
    if (busy || button.disabled) return;
    const id = Number(button.dataset.elSample);
    if (ids.has(id)) run(id);
  });
  document.addEventListener('eurus:cart:items-changed', event => {
    if (!event.detail?.elSamples) schedule();
  });
  document.addEventListener('eurus:cart:reload', schedule);
  // The native checkout validation event is synchronous. Stop while sample updates are pending.
  document.addEventListener('eurus:cart:validate', () => {
    if (busy && helper()) helper().validated = false;
  });
  document.addEventListener('submit', event => {
    if (busy && (event.submitter?.name === 'checkout' || /CartDrawer|cart/i.test(event.target.id))) {
      event.preventDefault();
      message = 'Your cart is updating. Please try checkout again in a moment.'; paint();
    }
  }, true);
  window.addEventListener('pageshow', event => { if (event.persisted) schedule(); });
  window.addEventListener('focus', schedule);
  document.addEventListener('shopify:section:load', schedule);
  // Read only once at startup; subsequent checks follow cart changes rather than polling.
  schedule();
})();
